import { Parser } from "json2csv";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import Incomes from "./income";
import { TABS } from "./tabs";

const CONTEXTS = ["push", "press", "all"];

/**
 * Requirements §4.1: the landing only appears when both products have
 * revenue — otherwise it's a tax on coaches with no gym. With one product
 * we redirect rather than render, so the URL always names its context and a
 * bookmark never re-runs this decision.
 */
function resolveContext(requested, productsWithRevenue = []) {
  const available = productsWithRevenue
    .map((p) => String(p).toLowerCase())
    .filter((p) => p === "push" || p === "press");

  if (requested && CONTEXTS.includes(requested)) {
    if (requested === "all" && available.length > 1) {
      return { context: "all", available };
    }
    if (available.includes(requested)) return { context: requested, available };
  }

  if (available.length > 1) return { context: null, available };
  if (available.length === 1) return { context: available[0], available };
  // A genuinely zero-products account (real first-sync/empty state, or a
  // stale `productsWithRevenue` cache on deploy day before the next
  // incremental sync recomputes it) falls back to the product-blind "All"
  // view — Milestone A's shape — rather than a dead-end blank landing.
  return { context: "all", available: [] };
}

const CSV_FIELDS = [
  "planName",
  "isPass",
  "activeCount",
  "unitsSold",
  "mrr",
  "revenueInPeriod",
  "newInPeriod",
  "cancelledInPeriod",
  "churnRate",
  "arpu",
  "ltv",
];

const IncomesController = async ({ res, req, load, locals, api }) => {
  const { query = {} } = req;
  const { selector = 3, granularity = "month" } = query;
  const tab = TABS.includes(query.tab) ? query.tab : "overview";
  const isOrg = !!locals.activeUser.organizationId;
  const organizationUser = locals.activeUserToRequest?._id;
  const syncQuery = isOrg ? { organizationUser } : {};

  // The Income page reads a ledger (lib/stripe-sync/, msb-api) kept warm by
  // backfill + webhooks + a nightly reconcile, not a live Stripe sweep — so
  // there's no "gate a slow page load" case for a synced account. The one
  // remaining wait is a brand-new connected account's first backfill, which
  // runs as a background job; this kicks it off and the view shows a
  // progress state until it finishes. Also POSTed while backfilling so the
  // API can re-enqueue one orphaned by a worker crash (it no-ops otherwise).
  let sync = await load("incomeSync", syncQuery);
  if (sync.status === "never") {
    await api("fe-income-sync", { method: "POST", query: syncQuery });
    sync = {
      ...sync,
      status: "backfilling",
      backfillStartedAt: new Date().toISOString(),
    };
  } else if (sync.status === "backfilling") {
    await api("fe-income-sync", { method: "POST", query: syncQuery });
  }
  const syncing = sync.status === "backfilling";

  const { context, available } = resolveContext(
    query.context,
    sync.productsWithRevenue
  );

  // A context that was asked for but isn't available (a stale bookmark, a
  // gym that stopped selling PUSH) resolves to something real above — send
  // the browser there so the URL and the screen agree.
  if (!syncing && context && query.context !== context) {
    // Built from an explicit allowlist, not `...query` — query.modal can be
    // an object (bracket-style `modal[type]=...` parsing, see
    // js/wrappers/share.js), and URLSearchParams silently stringifies an
    // object value to "[object Object]" instead of throwing. This redirect
    // is a canonicalization of the base URL; a modal that was open doesn't
    // need to survive it.
    const params = new URLSearchParams({
      ...(query.tab ? { tab: query.tab } : {}),
      ...(query.selector ? { selector: query.selector } : {}),
      ...(query.granularity ? { granularity: query.granularity } : {}),
      context,
    });
    return res.redirect(`/coaching-portal/income?${params.toString()}`);
  }

  const showLanding = !syncing && !context && available.length > 1;
  let landing = null;
  if (showLanding) {
    landing = await load("incomeLanding", syncQuery);
  }

  let plansCsv = "";
  if (!syncing && context) {
    if (tab === "overview") {
      await load(
        "feIncomes",
        isOrg ? { organizationUser, selector, context } : { selector, context }
      );
    } else if (tab === "plans") {
      const planPerformance = await load(
        "planPerformance",
        isOrg ? { organizationUser, selector, context } : { selector, context }
      );
      const parser = new Parser({ fields: CSV_FIELDS });
      plansCsv = parser.parse(planPerformance.plans || []);
    } else if (tab === "growth" && context !== "all") {
      await load(
        "growth",
        isOrg ? { organizationUser, selector, context } : { selector, context }
      );
    } else if (tab === "attention") {
      await load(
        "failedPayments",
        isOrg ? { organizationUser, context } : { context }
      );
    } else if (tab === "transactions") {
      await load(
        "transactions",
        isOrg ? { organizationUser, selector, context } : { selector, context }
      );
    } else if (tab === "payouts") {
      await load(
        "payouts",
        isOrg ? { organizationUser, selector, context } : { selector, context }
      );
    }
  }

  const modal = query.modal || {};

  locals.modal = {
    "activity-feed": <ActivityDrawer />,
  }[modal.type];
  res.body = (
    <Incomes
      syncing={syncing}
      sync={sync}
      syncQuery={syncQuery}
      tab={tab}
      selector={selector}
      granularity={granularity}
      plansCsv={plansCsv}
      api={api}
      context={context}
      contexts={available}
      landing={landing}
    />
  );
};

export default IncomesController;
